import { useState } from 'react';
import { PageLayoutsContainer } from '@wajeez-web/components/layouts';
import { HandleTableData } from '@wajeez-web/components/data-table';
import { AdminSystemPermissions } from '@wajeez-web/core/types';
import { DataTableWrapper } from '@wajeez-web/components/helpers';
import { contentEntityDataType } from '@wajeez-web/stores/content';
import { CheckBoxType } from '@wajeez-web/components/mui';
import { ConfirmationModal } from '@wajeez-web/components/ui';
import {
  EventKeyMappingsActions,
  useCustomerDispatch,
  useCustomerSelector,
} from '@wajeez-web/stores/customers';

import { EventKeyMappingModal } from '../components';

export const EventKeyMappings = () => {
  const customerDispatch = useCustomerDispatch();

  const [isEventKeyMappingModalOpen, setIsEventKeyMappingModalOpen] =
    useState(false);
  const [selectedEventKeyMapping, setSelectedEventKeyMapping] = useState<
    { id: number; published: boolean } | undefined
  >();
  const [confirmationModal, setConfirmationModal] = useState<{
    isOpen: boolean;
    message: string;
  }>({ isOpen: false, message: '' });

  const eventKeyMappingsDatatable = useCustomerSelector(
    ({ eventKeyMappings }) => eventKeyMappings.datatable
  );
  const changeEventKeyMappingPublishingStateStatus = useCustomerSelector(
    ({ eventKeyMappings }) =>
      eventKeyMappings.changeEventKeyMappingPublishingState.status
  );

  const { tablePreferances, setTablePreferences, isTableReferencesLoading } =
    HandleTableData(
      'eventKeyMappings',
      EventKeyMappingsActions.getEventKeyMappingsDatatableAsync
    );

  return (
    <div className="h-full">
      <PageLayoutsContainer
        title={`Events`}
        withoutRightPadding={false}
        breadCrumbsLinks={[{ title: 'Events' }]}
      >
        <div className="w-full">
          <DataTableWrapper
            data={eventKeyMappingsDatatable.data as contentEntityDataType}
            onHeaderManage={(header) => {
              const flags = header
                ?.filter((h) => h.selected)
                .reduce(
                  (previousValue: number, currentValue: CheckBoxType) =>
                    previousValue + Number(currentValue.value),
                  0
                );

              tablePreferances &&
                flags &&
                setTablePreferences({
                  ...tablePreferances,
                  flags: flags + 1,
                });
            }}
            onPaginationChange={(pagination) => {
              tablePreferances &&
                setTablePreferences({
                  ...tablePreferances,
                  pageNumber: pagination.pageNumber,
                  pageSize: pagination.pageSize,
                });
            }}
            sort={tablePreferances?.sortingOperations?.[0]}
            onSort={(sort) => {
              tablePreferances &&
                setTablePreferences({
                  ...tablePreferances,
                  sortingOperations: [sort],
                });
            }}
            onFilter={(filters, pagination) => {
              tablePreferances &&
                setTablePreferences({
                  ...tablePreferances,
                  pageNumber: pagination.pageNumber,
                  filteringOperations: filters.map((f) => {
                    return {
                      property: Number(f.key),
                      cretiria: f.value,
                      comparisonType: f.operator,
                    };
                  }),
                });
            }}
            isLoading={eventKeyMappingsDatatable.status === 'loading'}
            rowActions={(row) => {
              const published = row[8] === 'True';

              return [
                {
                  title: 'Edit',
                  endpoint: '',
                  permission: AdminSystemPermissions.eventKeyMappings.edit,
                  type: 'request',
                  action: () => {
                    setSelectedEventKeyMapping({
                      id: Number(row[1]) as number,
                      published: true,
                    });
                    setIsEventKeyMappingModalOpen(true);
                  },
                },
                {
                  title: published ? 'Unpublish' : 'Publish',
                  endpoint: '',
                  permission:
                    AdminSystemPermissions.eventKeyMappings
                      .changePublishingState,
                  type: 'request',
                  action: () => {
                    setSelectedEventKeyMapping({
                      id: Number(row[1]) as number,
                      published: published ? true : false,
                    });
                    setConfirmationModal({
                      isOpen: true,
                      message: `Are you sure that you want to ${
                        published ? 'unpublish' : 'publish'
                      } this event?`,
                    });
                  },
                },
              ];
            }}
            preferences={tablePreferances || undefined}
            setPreferences={setTablePreferences}
          />
        </div>
      </PageLayoutsContainer>
      {isEventKeyMappingModalOpen && selectedEventKeyMapping?.id && (
        <EventKeyMappingModal
          isOpen={isEventKeyMappingModalOpen}
          onClose={() => {
            setIsEventKeyMappingModalOpen(false);
          }}
          id={selectedEventKeyMapping.id}
          onSubmit={() => {
            setIsEventKeyMappingModalOpen(false);

            tablePreferances && setTablePreferences({ ...tablePreferances });
          }}
        />
      )}
      {confirmationModal.isOpen && (
        <ConfirmationModal
          isOpen={confirmationModal.isOpen}
          message={confirmationModal.message}
          onClose={() => {
            setConfirmationModal({ isOpen: false, message: '' });
          }}
          onSubmit={async () => {
            const res = await customerDispatch(
              EventKeyMappingsActions.changeEventKeyMappingPublishingStateAsync(
                {
                  id: selectedEventKeyMapping?.id,
                  published: !selectedEventKeyMapping?.published,
                }
              )
            );

            if (res.meta.requestStatus === 'fulfilled') {
              setConfirmationModal({ isOpen: false, message: '' });

              tablePreferances && setTablePreferences({ ...tablePreferances });
            }
          }}
          isLoading={changeEventKeyMappingPublishingStateStatus === 'loading'}
        />
      )}
    </div>
  );
};
